import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, ProgressBar } from 'react-bootstrap';
import { toast } from 'react-toastify';

interface PromptDescriptionModalProps {
  onModalClose?: () => void;
  setDescriptions?: Dispatch<SetStateAction<{ [key: number]: string }>>;
  currentDescription: string;
  setCurrentDescription: Dispatch<SetStateAction<string>>;
  editingIndex?: number | null;
  hideFooter?: boolean;
  onSubmit?: () => void;
  /** NEW: controls behavior/UX */
  mode?: 'view' | 'edit'; // defaults to 'edit'
}

const MAX_LEN = 200;

const PromptDescriptionModal: React.FC<PromptDescriptionModalProps> = ({
  onModalClose,
  setDescriptions,
  currentDescription,
  setCurrentDescription,
  editingIndex,
  hideFooter,
  onSubmit,
  mode = 'edit',
}) => {
  const isView = mode === 'view';
  const [error, setError] = useState<string>('');

  const length = currentDescription?.length || 0;
  const remaining = MAX_LEN - length;
  const progress = useMemo(() => Math.min(100, Math.round((length / MAX_LEN) * 100)), [length]);
  const isOverLimit = length > MAX_LEN;
  const canSubmit = !isOverLimit;

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isView) return; // no edits in view mode
    const value = e.target.value;
    setCurrentDescription(value);
    setError(value.length > MAX_LEN ? `Please keep it within ${MAX_LEN} characters.` : '');
  };

  const persistIfNeeded = () => {
    if (isView) return;
    if (editingIndex != null) {
      setDescriptions?.((prev) => ({
        ...prev,
        [editingIndex]: currentDescription,
      }));
    }
  };

  const handleSave = () => {
    if (isView) return; // no-op in view mode
    if (!canSubmit) return;
    persistIfNeeded();
    onSubmit?.();
    onModalClose?.();
    toast.success(`Description added successfully!`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isView) return;
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <>
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="mb-0">
            {isView ? 'Prompt Description' : 'Edit Prompt Description'}
          </Modal.Title>
          <small className="text-muted">
            {isView
              ? 'Context for this screening prompt'
              : 'A concise note that gives context to this screening prompt.'}
          </small>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-3">
        <Form.Group controlId="promptDescription">
          {!isView && (
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">
                Description <span className="text-muted">(optional)</span>
              </Form.Label>
              <small className={`${isOverLimit ? 'text-danger' : 'text-muted'}`}>
                {remaining} / {MAX_LEN}
              </small>
            </div>
          )}

          {isView ? (
            <div
              className="p-3 border rounded bg-light"
              style={{ minHeight: 140, whiteSpace: 'pre-wrap' }}
            >
              {currentDescription || <span className="text-muted">No description provided.</span>}
            </div>
          ) : (
            <>
              <Form.Control
                as="textarea"
                rows={6}
                placeholder="e.g., Enter prompt description"
                value={currentDescription}
                onChange={handleDescriptionChange}
                onKeyDown={handleKeyDown}
                isInvalid={isOverLimit}
                style={{ resize: 'vertical', minHeight: 140 }}
              />

              <div className="mt-2">
                <ProgressBar
                  now={progress}
                  variant={isOverLimit ? 'danger' : progress > 85 ? 'warning' : 'success'}
                  style={{ height: 6, borderRadius: 4 }}
                  aria-label="Character usage"
                />
              </div>

              {error && (
                <Alert variant="danger" className="mt-3 py-2 mb-0">
                  {error}
                </Alert>
              )}

              {!error && (
                <div className="text-muted small mt-3">
                  Tips: keep it skimmable, avoid PII, and focus on what a great answer should cover.
                  <br />
                  Shortcut: <kbd>Ctrl</kbd>/<kbd>⌘</kbd> + <kbd>Enter</kbd> to submit.
                </div>
              )}
            </>
          )}
        </Form.Group>
      </Modal.Body>

      {!hideFooter && (
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="light" onClick={onModalClose}>
            Close
          </Button>
          {!isView && (
            <Button variant="success" onClick={handleSave} disabled={!canSubmit}>
              Save
            </Button>
          )}
        </Modal.Footer>
      )}
    </>
  );
};

export default PromptDescriptionModal;
